/*globals VXConfig*/

'use strict';

import FluxEventEmitter from '../FluxEventEmitter';
import assign from 'object-assign';
import ActionCreator from '../actions/ActionCreator';
import Flux from '../Flux';
import Dispatcher from '../Dispatcher';
import Constants from '../Constants';
import EnumGA4Events from '../../utils/EnumGA4Events';
import { trackPurchase } from '../../utils/DataLayerHelper';
import { doVxqlRequest } from './VxqlStore/common.js';
import {
	StartAnonymousVoicecallChat,
	TouchMessengerChannel,
	GetUnreadMessagesCount,
	GetMessengerGifts,
	GetMessengerToyControls,
	GetMessengerContent,
	GetChannels,
} from './Messenger.Queries.js';

const ActionTypes = Constants.ActionTypes;

const ChangeEvents = {
	show: 'show',
	hide: 'hide',
	chatExit: 'chatExit',
	buyTicket: 'buyTicket',
	privateTicketShowInvitation: 'privateTicketShowInvitation',
};

let _isChatExitOpen = false;

function setIsChatExitOpen(isOpen) {
	_isChatExitOpen = isOpen;

	MessengerStore.emitChatExitEvent(isOpen);
}

function buyChatTicket(ticket, creationType = 'C') {
	if (ticket) {
		Flux.Vxql.buyChatTicket(ticket, creationType, (result) => {
			if (result.success) {
				const { show, ticket } = result;

				trackPurchase({
					[EnumGA4Events.DEFAULT_PARAMETER_TRANSACTION_ID]: ticket.id + ':' + show.id, // required, pseudo transaction id
					[EnumGA4Events.DEFAULT_PARAMETER_CURRENCY]: show.price.currency,
					[EnumGA4Events.DEFAULT_PARAMETER_VALUE]: show.price.value,
					[EnumGA4Events.DEFAULT_PARAMETER_ITEMS]: [
						{
							[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_ID]: ticket.id,
							[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_BRAND]: show.actorName,
							[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_NAME]:
								EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_CATEGORY__VALUE_CAM_SHOW_TICKET,
						},
					],
				});
			}

			MessengerStore.emitBuyTicketEvent(result);
		});
	}
}

function removeChatTicket(ticket) {
	if (ticket) {
		Flux.Vxql.removeChatTicket(ticket, (result) => {
			MessengerStore.emitBuyTicketEvent(result);
		});
	}
}

// Messenger-related VXQL functions migrated from VXQLStore

const MessengerStore = assign({}, FluxEventEmitter.prototype, {
	EVENTS: ChangeEvents,

	startChat: function (chatType, actorId) {
		const config = {
			chatType,
			actorId,
			mode: Flux.Constants.VXMessenger.Modes.WEBTOKEN,
		};
		// select channel to update sidebar
		ActionCreator.Messenger.selectChannelByActorId(actorId, true, false, false);

		this.emit(ChangeEvents.show, config);
	},
	startAnonymousVoicecallChat: function (actorId) {
		const onCreate = (result) => {
			const serverInfo = result.data.messenger.startAnonymousVoicecallChat;
			if (serverInfo && serverInfo.resultCode === 2000) {
				const config = {
					chatType: Flux.Constants.VXMessenger.ChatTypes.LIVECHAT,
					actorId: actorId,
					mode: Flux.Constants.VXMessenger.Modes.SERVER,
					serverInfo: { server: serverInfo.server, clientId: serverInfo.clientId },
				};

				this.emit(ChangeEvents.show, config);
			} else if (serverInfo.resultCode) {
				this.emit(ChangeEvents.show, {
					errorCode: serverInfo.resultCode,
					actorId: actorId,
				});
			}
		};

		const args = { actorId: actorId, accessToken: VXConfig.accessToken };
		doVxqlRequest(StartAnonymousVoicecallChat, args).then((result) => onCreate(result));
	},
	touchChannel: function (actorId) {
		return doVxqlRequest(TouchMessengerChannel, { actorId: actorId });
	},
	isChatExitOpen: function () {
		return _isChatExitOpen;
	},

	stopChat: function () {
		this.emit(ChangeEvents.hide, false);
	},

	emitChatExitEvent: function (isOpen) {
		this.emit(ChangeEvents.chatExit, isOpen);
	},

	emitBuyTicketEvent: function (result) {
		this.emit(ChangeEvents.buyTicket, result);
	},

	handlePrivateTicketShowInvitation: function (actorName, actorProfilePictureUrl, actorId) {
		this.emit(ChangeEvents.privateTicketShowInvitation, { actorName, actorProfilePictureUrl, actorId });
	},

	// VXQL methods migrated from VXQLStore
	getUnreadMessagesCount: function (callback) {
		doVxqlRequest(GetUnreadMessagesCount).then(callback);
	},
	getMessengerGifts: function (callback) {
		doVxqlRequest(GetMessengerGifts).then(callback);
	},
	getMessengerToyControls: function (callback) {
		doVxqlRequest(GetMessengerToyControls).then(callback);
	},
	getMessengerContent: function (first = 10, offset = 0, filter = null, order = '', callback) {
		const args = { first, offset, filter, order };
		doVxqlRequest(GetMessengerContent, args).then(callback);
	},
	getChannels: function (callback, blocked = null) {
		const args = {
			first: 500,
			offset: 0,
			filter: blocked !== null ? { blocked } : null,
		};
		doVxqlRequest(GetChannels, args).then(callback);
	},
});

MessengerStore.dispatchToken = Dispatcher.register(function (action) {
	switch (action.type) {
		case ActionTypes.Messenger.SET_IS_CHAT_EXIT_OPEN:
			setIsChatExitOpen(action.isOpen);
			break;
		case ActionTypes.Messenger.BUY_CHAT_TICKET:
			buyChatTicket(action.ticket, action.creationType);
			break;
		case ActionTypes.Messenger.REMOVE_CHAT_TICKET:
			removeChatTicket(action.ticket);
			break;
		default:
			break;
	}
});

export default MessengerStore;
